'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { CSSProperties } from 'react'
import { cx } from 'utils/cx'

import { cm2In } from '../../../utils/cm2in/cm2in'
import type { SizeGuideTableLayout } from '../../../utils/parseSizeGuide/parseSizeGuide'
import type { SizeGuidesListProps } from '../SizeGuidesList/SizeGuidesList'
import { SizeGuideCell } from './SizeGuideCell/SizeGuideCell'

import styles from './SizeGuideTable.module.scss'
import text from 'fukku/styles/texts.module.scss'

export type SizeGuideTableProps = {
	readonly sizeGuide: SizeGuideTableLayout
	readonly title?: string
	readonly showTitle?: boolean
} & Omit<SizeGuidesListProps, 'data'>

const CELL_DECIMALS_NUMBER = 2

export function SizeGuideTable({
	cellHeight,
	isHighlightable,
	isTransformable,
	sizeGuide,
	unit,
	title,
	showTitle = true,
}: SizeGuideTableProps): React.ReactNode {
	const {
		country: { locale },
	} = useMasterData()
	const numberFormat = Intl.NumberFormat(locale)

	const getCellValue = (value: string, cellIndex: number): string => {
		const parsedValue = Number(value)
		const units = {
			CM: numberFormat.format(parsedValue),
			PL: numberFormat.format(
				Number(cm2In(parsedValue).toFixed(CELL_DECIMALS_NUMBER))
			),
		}
		const isTransformableCell = unit && isTransformable && cellIndex !== 0
		return isTransformableCell ? units[unit] : value
	}

	return (
		<>
			{showTitle && title && (
				<p className={cx(text.bodyMRegular, styles.title)}>{title}</p>
			)}
			<div className={styles.container}>
				<table
					className={styles.table}
					style={{ '--cell-height': `${cellHeight}px` } as CSSProperties}
				>
					<thead>
						<tr>
							{sizeGuide.headers.map((cell) => (
								<th scope='col' className={styles.theader} key={cell}>
									<SizeGuideCell value={cell} className={text.titleM} />
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{sizeGuide.body.map((row) => {
							const rowId = row.map((rowData) => rowData.value).join('')

							return (
								<tr key={rowId}>
									{row.map((cell, cellIndex) => {
										const cellValue = getCellValue(cell.value, cellIndex)
										const id = `${sizeGuide.headers[cellIndex]}.${cellValue}`

										return (
											<td
												className={cx(
													styles.tdata,
													isHighlightable && styles.highlight
												)}
												key={id}
												data-testid={id}
											>
												<SizeGuideCell
													value={cellValue}
													description={cell.description}
													className={text.bodyM}
												/>
											</td>
										)
									})}
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</>
	)
}
